import { ComponentRef, PageRef } from '@wix/editor-platform-sdk-types';

import {
  addSearchResultsHeader,
  getSearchResultsComponent,
  getSearchResultsPage,
  updateSearchResultsPageData,
} from '../searchResults';

import { EditorAppContext } from '../types';
import { Interaction } from '../constants/interaction';
import { SEARCH_RESULTS_PAGE_URL_SLUG } from '../constants';
import { addSearchButton } from '../searchButton';
import { addSearchWidget } from '../addSearchWidget';
import { installSearchSuggestionsModal } from '../searchSuggestionsModal';
import { getConnectedSearchButtons } from '../getConnectedSearchButtons';
import { getConnectedSearchBoxes } from '../getConnectedSearchBoxes';

export async function doFirstInstall(appContext: EditorAppContext) {
  const { fedops, editorSDK, appDefinitionId } = appContext;
  fedops.interactionStarted(Interaction.InstallApp);

  const [searchResultsRef, searchResultsPageRef] = await Promise.all([
    getSearchResultsComponent(appContext),
    getSearchResultsPage(appContext),
  ]);

  if (!appContext.isHarmony) {
    await installSearchSuggestionsModal(editorSDK);
  }

  await editorSDK.pages.navigateTo(appDefinitionId, {
    pageRef: searchResultsPageRef,
  });

  const searchBoxRef = await installAppComponentsConsideringCE(
    appContext,
    searchResultsRef,
    searchResultsPageRef,
  );

  await editorSDK.pages.data.update(appDefinitionId, {
    pageRef: searchResultsPageRef,
    data: {
      pageUriSEO: SEARCH_RESULTS_PAGE_URL_SLUG,
      managingAppDefId: appDefinitionId,
    },
  });

  if (searchBoxRef) {
    await editorSDK.selection.selectComponentByCompRef(appDefinitionId, {
      compsToSelect: [searchBoxRef],
    });
  }

  fedops.interactionEnded(Interaction.InstallApp);
}

async function addSearchEntryComponent(appContext: EditorAppContext) {
  if (appContext.isHarmony) {
    return addSearchWidget(appContext);
  }
  const searchBoxesControllers = await getConnectedSearchBoxes(appContext);
  const searchButtonControllers = await getConnectedSearchButtons(appContext);
  if (searchBoxesControllers.length > 0 || searchButtonControllers.length > 0) {
    return undefined;
  }
  return addSearchButton(appContext);
}

async function installAppComponents(
  appContext: EditorAppContext,
  searchResultsRef: ComponentRef | undefined,
  searchResultsPageRef: PageRef,
) {
  const searchComponent = await addSearchEntryComponent(appContext);

  if (searchResultsRef && !appContext.isHarmony) {
    await addSearchResultsHeader(appContext, {
      searchResultsPageRef,
      searchResultsRef,
    });
  }

  await updateSearchResultsPageData(appContext, {
    searchResultsPageRef,
  });

  return searchComponent;
}

async function installAppComponentsConsideringCE(
  appContext: EditorAppContext,
  searchResultsRef: ComponentRef | undefined,
  searchResultsPageRef: PageRef,
) {
  const { editorSDK, reportError, appDefinitionId } = appContext;
  try {
    return await editorSDK.document.transactions.runAndWaitForApproval(
      appDefinitionId,
      async () => {
        await installAppComponents(
          appContext,
          searchResultsRef,
          searchResultsPageRef,
        );
      },
    );
  } catch (error) {
    reportError(error);
  }
}
